import React, { useEffect, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Spinner,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Label
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardPageHeader } from "../../../Components/Common/DashboardKit";
import api from "../../../api";

const TaskDetails = () => {
    document.title = "Task Details | SSW Technologies Dashboard";

    const navigate = useNavigate();
    const { taskId } = useParams();

    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    const currentEmpId = authUser?.uid;

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Round TM-3 (2026) — task notes (legacy TaskReview "Notes" tab).
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState("");
    const [noteSaving, setNoteSaving] = useState(false);

    // Round TM-11 — Work Reporting + Timing Totals (legacy TaskReview tabs).
    const [laborRows, setLaborRows] = useState([]);
    const [laborTotals, setLaborTotals] = useState([]);

    const loadLabor = async () => {
        try {
            const [rowsRes, totalsRes] = await Promise.all([
                api.get(`/TaskManagement/tasks/${taskId}/labor`),
                api.get(`/TaskManagement/tasks/${taskId}/labor-totals`),
            ]);
            const rows = rowsRes?.data ?? rowsRes;
            const totals = totalsRes?.data ?? totalsRes;
            setLaborRows(Array.isArray(rows) ? rows : []);
            setLaborTotals(Array.isArray(totals) ? totals : []);
        } catch (err) {
            console.error("Failed to load labor:", err);
        }
    };

    // Round TM-11 — Reassign modal state.
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignBusy, setReassignBusy] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [reassignIds, setReassignIds] = useState([]);
    const [removeRejected, setRemoveRejected] = useState(false);

    const openReassign = async () => {
        setReassignOpen(true);
        setReassignIds([]);
        setRemoveRejected(false);
        if (employees.length === 0) {
            try {
                const res = await api.get("/registration/employees");
                const data = res?.data ?? res ?? [];
                setEmployees(
                    (Array.isArray(data) ? data : []).filter((e) => e.status === "A")
                );
            } catch (err) {
                console.error("Failed to load employees:", err);
            }
        }
    };

    const handleReassignSubmit = async () => {
        try {
            setReassignBusy(true);
            setError("");
            const res = await api.post(`/TaskManagement/tasks/${taskId}/reassign`, {
                addEmpIds: reassignIds,
                removeRejected,
            });
            const data = res?.data ?? res;
            if (data?.message) window.alert(data.message);
            setReassignOpen(false);
            await reloadTask();
            await loadLabor();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to reassign task.");
        } finally {
            setReassignBusy(false);
        }
    };

    const handleCloseTask = async () => {
        if (!window.confirm(
            "Close this task? Every assignment will be marked complete."
        )) return;
        try {
            setLoading(true);
            setError("");
            const res = await api.post(`/TaskManagement/tasks/${taskId}/close`, {});
            const data = res?.data ?? res;
            if (data?.message) window.alert(data.message);
            await reloadTask();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to close task.");
        } finally {
            setLoading(false);
        }
    };

    const handleRequestRevision = async () => {
        const completed = (task?.assignees || []).filter((a) => a.userMarkComplete);
        if (completed.length === 0) {
            setError("No completed assignment to reopen.");
            return;
        }
        let target = String(completed[0].assignTo || completed[0].empId);
        if (completed.length > 1) {
            const pick = window.prompt(
                "Request a revision from which employee?\n" +
                completed
                    .map((a) => `${a.assignTo || a.empId} — ${a.name || a.assignToName || ""}`)
                    .join("\n") +
                "\n\nEnter the employee number:",
                target
            );
            if (pick === null) return;
            target = pick.trim();
        }
        const suggestion = window.prompt(
            "Describe the revision you need (required):"
        );
        if (suggestion === null) return;
        if (!suggestion.trim()) {
            setError("A suggestion is required to request a revision.");
            return;
        }
        try {
            setLoading(true);
            setError("");
            const res = await api.post(`/TaskManagement/tasks/${taskId}/request-revision`, {
                assigneeEmpId: target,
                suggestion: suggestion.trim(),
            });
            const data = res?.data ?? res;
            if (data?.message) window.alert(data.message);
            await reloadTask();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to request revision.");
        } finally {
            setLoading(false);
        }
    };

    const loadNotes = async () => {
        try {
            const res = await api.get(`/TaskManagement/tasks/${taskId}/memos`);
            const data = res?.data ?? res;
            setNotes(Array.isArray(data) ? data : []);
        } catch (err) {
            // Notes are non-blocking; leave the list empty on failure.
            console.error("Failed to load task notes:", err);
        }
    };

    const handleAddNote = async () => {
        if (!noteText.trim()) return;
        try {
            setNoteSaving(true);
            await api.post(`/TaskManagement/tasks/${taskId}/memos`, {
                text: noteText.trim()
            });
            setNoteText("");
            await loadNotes();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to add note.");
        } finally {
            setNoteSaving(false);
        }
    };

    useEffect(() => {
        const fetchTaskDetails = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await api.get(`/TaskManagement/task-details/${taskId}`, {
                    params: { empId: currentEmpId }
                });

                const data = response?.data ?? response;
                setTask(data);
            } catch (err) {
                console.error("Failed to load task details:", err);
                setError(
                    err?.response?.data?.message ||
                    err?.response?.data ||
                    "Unable to load task details."
                );
            } finally {
                setLoading(false);
            }
        };

        if (taskId && currentEmpId) {
            fetchTaskDetails();
            loadNotes();
            loadLabor();
        }
    }, [taskId, currentEmpId]);

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleDateString();
    };

    const formatDateTime = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleString();
    };
    const currentAssignee = task?.assignees?.find(
        (a) => String(a.assignTo || a.empId) === String(currentEmpId)
    );

    const isCompleted = !!currentAssignee?.userMarkComplete;
    const isRejected = !!currentAssignee?.rejected;
    const hasOpenLog = !!task?.hasOpenLog;
    const taskStatus = task?.status?.toUpperCase();

    const canReopen = taskStatus === "ACTION_REQUIRED";

    // Round TM-11 (2026) — creator powers (legacy TaskReview) + Meeting
    // lockdown (legacy disabled complete/reject/due-date on meetings).
    const isCreator = String(task?.assignedBy || "").trim() === String(currentEmpId);
    const isMeeting = (task?.type || "").toLowerCase() === "meeting";

    const disableStartStop = hasOpenLog
        ? false
        : ((isCompleted || isRejected) && !canReopen);

    const disableComplete = isMeeting
        ? true
        : hasOpenLog
            ? false
            : ((isCompleted || isRejected) && !canReopen);

    const disableReject = isMeeting
        || hasOpenLog
        || ((isCompleted || isRejected) && !canReopen);

    const handleDownloadAttachment = async (attachmentId, fileName) => {
        try {
            const response = await api.get(
                `/TaskManagement/task-attachment/${attachmentId}/download`,
                {
                    responseType: "blob"
                }
            );

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName || "attachment");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download attachment.");
        }
    };

    // Task Start / End / Complete / Reject

    const handleStartTask = async (closeOtherSessions = false) => {
        try {
            setLoading(true);
            setError("");

            await api.post(`/TaskManagement/tasks/${taskId}/start`, {
                empId: currentEmpId,
                employeeName: authUser?.name || authUser?.fullName || "",
                startTimeUserStated: new Date().toISOString(),
                description: "",
                closeOtherSessions
            });

            await reloadTask();
        } catch (err) {
            const msg = err?.response?.data?.message || "";
            // Round TM-5 — legacy one-open-session rule: the server
            // answers OPEN_SESSION:{taskId}:{text}; confirm, then retry
            // with closeOtherSessions so the other log is closed at "now".
            if (msg.startsWith("OPEN_SESSION:")) {
                const parts = msg.split(":");
                const text = parts.slice(2).join(":");
                if (window.confirm(text)) {
                    setLoading(false);
                    return handleStartTask(true);
                }
                setError("");
            } else {
                setError(msg || "Unable to start task.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStopTask = async () => {
        try {
            setLoading(true);
            setError("");

            await api.post(`/TaskManagement/tasks/${taskId}/stop`, {
                empId: currentEmpId,
                endTimeUserStated: new Date().toISOString(),
                description: ""
            });

            await reloadTask();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to stop task logging.");
        } finally {
            setLoading(false);
        }
    };

    const handleMarkComplete = async () => {
        try {
            setLoading(true);
            setError("");

            await api.post(`/TaskManagement/tasks/${taskId}/complete`, {
                empId: currentEmpId
            });

            await reloadTask();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to mark complete.");
        } finally {
            setLoading(false);
        }
    };

    // Round TM-2 (2026) — legacy "Mark Not Complete" parity: an assignee
    // can withdraw their own completion (reverts a 56-status ECN to 55
    // server-side).
    const handleNotComplete = async () => {
        try {
            setLoading(true);
            setError("");

            await api.post(`/TaskManagement/tasks/${taskId}/not-complete`, {
                empId: currentEmpId
            });

            await reloadTask();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to mark not complete.");
        } finally {
            setLoading(false);
        }
    };

    // Round TM-3 (2026) — legacy parity: a rejection REQUIRES a reason
    // (legacy InputBox refused an empty one). The reason is stored as a
    // task note and emailed to the creator server-side.
    const handleRejectTask = async () => {
        const reason = window.prompt(
            "Please provide the reason for rejecting this task (required):"
        );
        if (reason === null) return;            // user cancelled
        if (!reason.trim()) {
            setError("A reason is required to reject a task.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await api.post(`/TaskManagement/tasks/${taskId}/reject`, {
                empId: currentEmpId,
                reason: reason.trim()
            });

            await reloadTask();
        } catch (err) {
            setError(err?.response?.data?.message || "Unable to reject task.");
        } finally {
            setLoading(false);
        }
    };

    const reloadTask = async () => {
        const response = await api.get(`/TaskManagement/task-details/${taskId}`, {
            params: { empId: currentEmpId }
        });

        const data = response?.data ?? response;
        setTask(data);
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-file-list-3-line"
                    title="Task Details"
                    subtitle="Activity feed, attachments, and status changes"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Task Details" },
                    ]}
                />

                {error && (
                    <Alert color="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <div className="mt-2 text-muted">Loading task details...</div>
                    </div>
                ) : task ? (
                    <Row>
                        <Col xl={8}>
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-start">
                                    <div>
                                        <h4 className="mb-1">{task.description || "Task Details"}</h4>
                                        <p className="text-muted mb-0">
                                            Task ID: <span className="fw-semibold">{task.taskId}</span>
                                        </p>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {/* Round DMS-UAT-8 — legacy Task-window parity: the
                                            WinForms Task form had an
                                            "ECN-xxxxx Task - Click to View ECN" button. When
                                            this task belongs to a DMS document, jump to the
                                            document preview (the ?openDmsId deep link opens
                                            the DmsViewModal on the All Documents page). */}
                                        {task.dmsId && (
                                            <Button
                                                color="primary"
                                                outline
                                                size="sm"
                                                onClick={() => navigate(`/document-management?openDmsId=${task.dmsId}`)}
                                                title={`Open ${task.dmsDocId || `document #${task.dmsId}`}`}
                                            >
                                                <i className="ri-file-text-line align-bottom me-1"></i>
                                                View {task.dmsDocId || "Document"}
                                            </Button>
                                        )}
                                        {/* Round TM-11 — legacy "View Project"
                                            parity: surface the ProjectID link
                                            when the task belongs to a project. */}
                                        {task.projectId ? (
                                            <Badge color="info" pill title="This task belongs to a project">
                                                Project #{task.projectId}
                                            </Badge>
                                        ) : null}
                                        {isCreator && !isMeeting && (
                                            <>
                                                <Button
                                                    color="success"
                                                    outline
                                                    size="sm"
                                                    onClick={handleCloseTask}
                                                    title="Mark the whole task complete (legacy 'Close Task')"
                                                >
                                                    Close Task
                                                </Button>
                                                <Button
                                                    color="warning"
                                                    outline
                                                    size="sm"
                                                    onClick={handleRequestRevision}
                                                    title="Reopen a completed assignment with a suggestion"
                                                >
                                                    Request Revision
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    outline
                                                    size="sm"
                                                    onClick={openReassign}
                                                >
                                                    Reassign
                                                </Button>
                                            </>
                                        )}
                                        <Badge color={task.status === "ACTIVE" ? "success" : "secondary"}>
                                            {task.status || "Unknown"}
                                        </Badge>
                                    </div>
                                </CardHeader>

                                <CardBody>
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="text-muted small">Due Date</div>
                                            <div className="fw-semibold">{formatDate(task.dueDate)}</div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="text-muted small">Created On</div>
                                            <div className="fw-semibold">{formatDateTime(task.createdOn)}</div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="text-muted small">Assigned By</div>
                                            <div className="fw-semibold">{task.assignedByName || "-"}</div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="text-muted small">Task Type</div>
                                            <div className="fw-semibold">{task.type || "-"}</div>
                                        </Col>

                                        <Col md={12}>
                                            <div className="text-muted small">Pertinent Info / Comments</div>
                                            <div className="fw-semibold">
                                                {task.comments || task.pertinentInfo || "-"}
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0">
                                    <h5 className="mb-0">Attachments</h5>
                                </CardHeader>
                                <CardBody>
                                    {task.attachments && task.attachments.length > 0 ? (
                                        <ListGroup flush>
                                            {task.attachments.map((att) => (
                                                <ListGroupItem
                                                    key={att.id || att.attachmentId}
                                                    className="d-flex justify-content-between align-items-center px-0"
                                                >
                                                    <div>
                                                        <div className="fw-semibold">
                                                            {att.attachmentFilename || att.fileName}
                                                        </div>
                                                        <div className="text-muted small">
                                                            Added on {formatDateTime(att.timestamp || att.uploadedOn)}
                                                        </div>
                                                    </div>

                                                    <Button
                                                        size="sm"
                                                        color="light"
                                                        onClick={() =>
                                                            handleDownloadAttachment(
                                                                att.id || att.attachmentId,
                                                                att.attachmentFilename || att.fileName
                                                            )
                                                        }
                                                    >
                                                        Download
                                                    </Button>
                                                </ListGroupItem>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <div className="text-muted">No attachments available.</div>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Round TM-3 (2026) — Notes (legacy TaskReview
                                "Notes" tab). Rejection reasons also land
                                here automatically. */}
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Notes</h5>
                                    <Badge color="light" className="text-muted">{notes.length}</Badge>
                                </CardHeader>
                                <CardBody>
                                    <div className="d-flex gap-2 mb-3">
                                        <textarea
                                            className="form-control"
                                            rows={2}
                                            placeholder="Add a note for this task..."
                                            value={noteText}
                                            onChange={(e) => setNoteText(e.target.value)}
                                        />
                                        <Button
                                            color="primary"
                                            disabled={noteSaving || !noteText.trim()}
                                            onClick={handleAddNote}
                                        >
                                            {noteSaving ? "Saving..." : "Add"}
                                        </Button>
                                    </div>

                                    {notes.length > 0 ? (
                                        <ListGroup flush>
                                            {notes.map((n) => (
                                                <ListGroupItem key={n.id} className="px-0">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="fw-semibold">{n.enteredBy}</span>
                                                        <span className="text-muted small">
                                                            {formatDateTime(n.timestamp)}
                                                        </span>
                                                    </div>
                                                    <div style={{ whiteSpace: "pre-wrap" }}>{n.memo}</div>
                                                </ListGroupItem>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <div className="text-muted">No notes yet.</div>
                                    )}
                                </CardBody>
                            </Card>

                            {/* Round TM-11 — legacy TaskReview "Work Reporting"
                                and "Timing Totals" tabs. */}
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0 d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">Work Reporting</h5>
                                    <Badge color="light" className="text-muted">{laborRows.length}</Badge>
                                </CardHeader>
                                <CardBody>
                                    {laborRows.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table size="sm" className="align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Employee</th>
                                                        <th>Start</th>
                                                        <th>End</th>
                                                        <th className="text-end">Minutes</th>
                                                        <th>Description of Work</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {laborRows.map((r) => (
                                                        <tr key={r.id}>
                                                            <td>{r.name || r.empId}</td>
                                                            <td>{formatDateTime(r.start)}</td>
                                                            <td>{r.end ? formatDateTime(r.end) : <Badge color="warning">Open</Badge>}</td>
                                                            <td className="text-end">{r.minutes}</td>
                                                            <td style={{ whiteSpace: "pre-wrap" }}>{r.descriptionOfWork || "-"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-muted">No time has been logged on this task.</div>
                                    )}
                                </CardBody>
                            </Card>

                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0">
                                    <h5 className="mb-0">Timing Totals</h5>
                                </CardHeader>
                                <CardBody>
                                    {laborTotals.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table size="sm" className="align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Assignee</th>
                                                        <th className="text-end">Total Minutes</th>
                                                        <th className="text-end">Hours</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {laborTotals.map((r) => (
                                                        <tr key={r.empId}>
                                                            <td>{r.name || r.empId}</td>
                                                            <td className="text-end">{r.totalMinutes}</td>
                                                            <td className="text-end">{(r.totalMinutes / 60).toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-muted">No assignees.</div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={4}>
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0">
                                    <h5 className="mb-0">Assignees</h5>
                                </CardHeader>
                                <CardBody>
                                    {task.assignees && task.assignees.length > 0 ? (
                                        <div className="d-flex flex-column gap-2">
                                            {task.assignees.map((assignee, index) => (
                                                <div
                                                    key={assignee.empId || assignee.assignTo || index}
                                                    className="border rounded px-3 py-2"
                                                >
                                                    <div className="fw-semibold">
                                                        {assignee.name || assignee.assigneeName || assignee.assignTo}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {assignee.rejected
                                                            ? "Rejected"
                                                            : assignee.userMarkComplete
                                                                ? "Completed"
                                                                : "Open"}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-muted">No assignees found.</div>
                                    )}
                                </CardBody>
                            </Card>

                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0">
                                    <h5 className="mb-0">Actions</h5>
                                </CardHeader>
                                <CardBody className="d-flex flex-column gap-2">
                                    <Button
                                        color="primary"
                                        disabled={disableStartStop}
                                        onClick={() => (hasOpenLog ? handleStopTask() : handleStartTask())}
                                    >
                                        {hasOpenLog ? "Stop Task Logging" : "Start Task Logging"}
                                    </Button>

                                    <Button
                                        color="success"
                                        disabled={disableComplete}
                                        onClick={handleMarkComplete}
                                    >
                                        Mark Complete
                                    </Button>

                                    {isCompleted && (
                                        <Button
                                            color="warning"
                                            outline
                                            onClick={handleNotComplete}
                                        >
                                            Mark Not Complete
                                        </Button>
                                    )}

                                    <Button
                                        color="danger"
                                        outline
                                        disabled={disableReject}
                                        onClick={handleRejectTask}
                                    >
                                        Reject Task
                                    </Button>

                                    <Button
                                        color="light"
                                        onClick={() => navigate("/task-management")}
                                    >
                                        Back to My Tasks
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                ) : null}
                <Modal isOpen={reassignOpen} toggle={() => setReassignOpen(false)}>
                    <ModalHeader toggle={() => setReassignOpen(false)}>
                        Reassign Task #{task?.taskId}
                    </ModalHeader>
                    <ModalBody>
                        <Label className="form-label">
                            Add assignees (Ctrl/Cmd-click for multiple — 6 max per task)
                        </Label>
                        <select
                            multiple
                            size={10}
                            className="form-select"
                            value={reassignIds}
                            onChange={(e) =>
                                setReassignIds(
                                    Array.from(e.target.selectedOptions).map((o) => o.value)
                                )
                            }
                        >
                            {employees.map((emp) => (
                                <option key={emp.empId} value={String(emp.empId)}>
                                    {emp.name} ({emp.empId})
                                </option>
                            ))}
                        </select>
                        <div className="form-check mt-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="removeRejectedChk"
                                checked={removeRejected}
                                onChange={(e) => setRemoveRejected(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="removeRejectedChk">
                                Remove rejected assignees from this task
                            </label>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setReassignOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            disabled={reassignBusy || (reassignIds.length === 0 && !removeRejected)}
                            onClick={handleReassignSubmit}
                        >
                            {reassignBusy ? "Saving..." : "Reassign"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default TaskDetails;